import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faLeaf, faReply } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useRef } from 'react';
import StarRating from '~/components/shared/StarRating';
import Tooltip from '~/components/ui/Tooltip';
import { formatDate } from '~/helpers/helperFunction';
import { searchQueryFromUrl } from '~/helpers/projectConstant';
import { cn } from '~/helpers/utils';
import { Reply, Review, ReviewLocation } from '~/types/Review';
import ReviewReplyAvatar from './Reply/ReplyAvatar';

interface ReviewCardProps extends ReviewCardContentProps {
  type?: ReviewCardType;
  className?: string;
  hideReplies?: boolean;
}

interface ReviewCardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  key?: React.Key;
  review: Review;
  scope?: string;
  transformDate?: boolean;
  isLink?: boolean;
  hideDetails?: boolean;
}

interface ReviewReplyContentProps extends React.HTMLAttributes<HTMLDivElement> {
  reply: Reply;
  location?: ReviewLocation;
  showFullContent?: boolean;
  showReactionLogo?: boolean;
}

export enum ReviewCardType {
  Link,
  List,
}

const ReviewCard: React.FC<ReviewCardProps> = ({
  className,
  hideReplies,
  ...otherProps
}) => {
  const scope = otherProps.scope ?? 'advies';

  if (otherProps.type == ReviewCardType.List) {
    return (
      <ReviewCardContent className="review-card" isLink={false} {...otherProps}>
        {!hideReplies && otherProps.review.reply && (
          <ReviewReplyContent
            reply={otherProps.review.reply}
            location={otherProps.review.location}
            showFullContent={true}
            showReactionLogo={true}
          />
        )}
      </ReviewCardContent>
    );
  }

  const location = otherProps.review.location;
  const zipcode = location?.zipCode ?? location?.address?.zipcode;
  return (
    <a
      href={`/${scope}/${zipcode}/${location?.slug}/beoordeling/${otherProps.review.slug}${searchQueryFromUrl ?? ''}`}
      className={cn('review-card review-card--link group', className)}
    >
      <ReviewCardContent isLink={true} {...otherProps}>
        {!hideReplies && otherProps.review.reply && (
          <ReviewReplyContent
            reply={otherProps.review.reply}
            className="hidden md:block"
            location={otherProps.review.location}
          />
        )}
      </ReviewCardContent>
    </a>
  );
};

const ReviewCardContent: React.FC<ReviewCardContentProps> = ({
  className,
  review,
  scope,
  transformDate,
  isLink,
  hideDetails,
  children,
}) => {
  const {
    slug,
    author,
    content,
    date,
    scopeName,
    score,
    location,
    hasSustainability,
    state,
  } = review;

  const zipcode = location?.zipCode ?? location?.address?.zipcode;
  scope ??= 'advies';

  const hoverRef = useRef<HTMLDivElement>(null);
  const detailsUrl = `/${scope}/${zipcode}/${location?.slug}/beoordeling/${slug}${searchQueryFromUrl ?? ''}`;

  return (
    <div className={className}>
      <div className="flex w-full items-center justify-between">
        <h4 className="panel-title mb-2.5 font-roboto text-lg font-black md:truncate md:text-xl">
          {!isLink && !hideDetails ? (
            <a href={detailsUrl} className="block no-underline">
              {author}
            </a>
          ) : (
            author
          )}
        </h4>

        {isLink ? (
          <span className="review-card__details-link">details</span>
        ) : !hideDetails ? (
          <a href={detailsUrl} className="review-card__details-link">
            details
          </a>
        ) : null}
      </div>
      <div className="mb-[10px] flex flex-row justify-between">
        <StarRating score={score ?? 0} />
        <div className="max-h-[25px] rounded-[24px] bg-sand-100 px-[10px] py-[4px]">
          <p className="font-roboto text-xs font-medium text-black">
            {scopeName}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="body-color mb-2.5 font-roboto text-xs font-bold leading-4 text-stone-700">
          {transformDate ? formatDate(date) : date}
        </div>
        {hasSustainability && (
          <div>
            <div
              ref={hoverRef}
              className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-stone-100"
            >
              <FontAwesomeIcon
                icon={faLeaf as IconProp}
                className="text-sm text-green"
              />
            </div>
            <Tooltip hoverRef={hoverRef}>
              <FontAwesomeIcon
                icon={faLeaf as IconProp}
                className="text-sm text-green"
              />
              De reviewgever heeft aangegeven dat verduurzaming is besproken
              door de medewerker van het advieskantoor.
            </Tooltip>
          </div>
        )}
      </div>
      <div className="mb-auto w-full overflow-hidden">
        <p className="body-color whitespace-pre-line font-roboto text-sm font-normal text-blue-900 md:line-clamp-4">
          {content}
        </p>
      </div>

      {children}

      {state === 'KlantBevestigd' && (
        <div className="body-color mt-3 overflow-hidden font-roboto text-sm font-normal italic text-stone-500">
          <strong>Let op:</strong> deze review is nog niet door Advieskeuze op
          echtheid gecontroleerd.
        </div>
      )}

      {state === 'InOnderzoek' && (
        <div className="mt-3 overflow-hidden font-roboto text-sm font-normal italic text-stone-500">
          <strong>Let op:</strong> deze review wordt onderzocht door
          Advieskeuze.
        </div>
      )}
    </div>
  );
};

const ReviewReplyContent: React.FC<ReviewReplyContentProps> = ({
  reply,
  location,
  showFullContent,
  showReactionLogo,
  className,
}) => {
  return (
    <div className={cn(className, 'rounded-2 rounded-lg bg-sand-50 p-4 pt-5')}>
      <div className="flex flex-row items-start justify-start gap-1">
        <FontAwesomeIcon
          icon={faReply as IconProp}
          className="text-blue"
          rotate={180}
        />
        <div>
          <h2 className="mb-2.5 font-roboto text-sm font-bold text-stone-900">
            Reactie van {location?.name}
          </h2>
          <p className="font-roboto text-sm text-stone-700">
            {reply.content.length > 180 && !showFullContent
              ? `${reply.content.slice(0, 180)}...`
              : reply.content}
          </p>
          {location && showReactionLogo && (
            <div>
              <div className="flex items-center gap-3 pt-4">
                {!!location.logo && (
                  <ReviewReplyAvatar
                    logo={location.logo}
                    name={location.name}
                  />
                )}
                <div className="font-bold">{location.name}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewCard;
